import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import ToDoListWrapper from "./index.style";
import { useIntl } from "react-intl";
import { Layout, Typography } from "antd";
import ToDoSider from "./ToDoSider";
import ToDoHeader from "./ToDoHeader";
import TasksList from "./TasksList";
import moment from "moment";
import { useCrm_task_statuses } from "../../../../hooks";
import { fetchList } from "../../../../api/server";
import { useQuery } from "react-query";

import EditActivity from "../activity/EditActivity";
import { BellOutlined } from "@ant-design/icons";
import { Badge, Button, Drawer } from "antd";

import {
  setTasksFilters,
  setTasksQueryFilters,
} from "../../../../features/crm/activities/activitiesSlice";
import { useDispatch, useSelector } from "react-redux";
const { Text } = Typography;
const { Content } = Layout;

const variants = {
  open: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.15, delay: 0.3 },
  },
  closed: {
    scale: 1,
    opacity: 0,
    transition: { delay: 0.1, duration: 0 },
  },
};

const ToDoList = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [contentTitle, setContentTitle] = useState("");
  const [activeRecord, setActiveRecord] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [childrenDrawer, setChildrenDrawer] = useState(false);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(null);
  const createTaskLoading = useSelector(
    (state) => state.pendingActions["activity/create"]
  );
  const updateTaskLoading = useSelector(
    (state) => state.pendingActions["activity/update"]
  );
  const completeTaskLoading = useSelector(
    (state) => state.pendingActions["activity/complete_task"]
  );

  const [tasksReloadTimestamp, setTasksReloadTimestamp] = useState(
    moment().format()
  );
  const filters = useSelector((state) => state.activity.tasksFilters);
  const tasksFilter = useSelector((state) => state.activity.tasksQueryFilters);
  const statuses = useCrm_task_statuses();
  const intl = useIntl();
  const openStatus =
    statuses &&
    statuses.length > 0 &&
    statuses.filter((i) => i.name === "open")[0].id;
  const initialLimit = 10;

  useEffect(() => {
    setLimit(initialLimit);
  }, [isOpen, tasksFilter, filters]);

  useEffect(() => {
    if (
      loading &&
      !(createTaskLoading || updateTaskLoading || completeTaskLoading)
    ) {
      setTasksReloadTimestamp(moment().format());
    }
    setLoading(createTaskLoading || updateTaskLoading || completeTaskLoading);
  }, [createTaskLoading, updateTaskLoading, completeTaskLoading]);

  useEffect(() => {
    const filter = { $and: [] };

    if (filters.priority) {
      filter["$and"].push({ priority_id: filters.priority });
    }
    if (filters.starred) {
      filter["$and"].push({ has_star: filters.starred });
    }
    if (filters.status) {
      filter["$and"].push({ status_id: filters.status });
    }
    if (filters.due_date) {
      switch (filters.due_date) {
        case "all":
          break;
        case "today":
          filter["$and"].push({
            $and: [
              {
                due_date: {
                  $gte: moment().startOf("day").format(),
                },
              },
              {
                due_date: {
                  $lte: moment().endOf("day").format(),
                },
              },
            ],
          });
          break;
        case "lastTwoWeeks":
          filter["$and"].push({
            $and: [
              {
                due_date: {
                  $gte: moment().subtract(2, "week").startOf("day").format(),
                },
              },
              {
                due_date: {
                  $lte: moment().endOf("day").format(),
                },
              },
            ],
          });
          break;
        case "nextWeek":
          filter["$and"].push({
            due_date: {
              $gte: moment().startOf("day").format(),
            },
          });
          filter["$and"].push({
            due_date: {
              $lte: moment().add(1, "week").endOf("day").format(),
            },
          });
          break;
        case "overdue":
          filter["$and"].push({
            $and: [
              {
                status_id: openStatus,
              },
              {
                due_date: {
                  $lte: moment().subtract(1, "day").endOf("day").format(),
                },
              },
            ],
          });
          break;
        default:
          break;
      }
    }
    filter["$and"].push({
      type: {
        $eq: "task",
      },
    });
    if (filters.due_date) {
      dispatch(setTasksQueryFilters(filter));
    }
  }, [filters]);

  const changeContentTitle = (arr) => {
    let t = intl.formatMessage({
      id: "app.todolist.noFilters",
    });
    if (arr.length > 0) {
      t = arr.join(" | ");
    }
    setContentTitle(t);
  };

  const minimize = () => {
    setIsOpen(false);
  };
  const onChangeFilters = (changed, all) => {
    if (changed["due_date"]) {
      dispatch(setTasksFilters({ ...all, starred: filters.starred }));
    } else {
      dispatch(
        setTasksFilters({
          ...all,
          due_date: filters.due_date,
          starred: filters.starred,
        })
      );
    }
  };

  const onChangeMenuFilter = (val) => {
    if (val === "starred") {
      dispatch(setTasksFilters({ ...filters, starred: true }));
    } else {
      dispatch(setTasksFilters({ ...filters, due_date: val, starred: false }));
    }
  };

  const tasksPage = {
    current: 1,
    filter: tasksFilter,
    order: { field: "due_date", order: "descend" },
    pageSize: limit,
  };

  const { isLoading, data } = useQuery(
    [tasksPage, tasksReloadTimestamp, isOpen],
    async () => {
      const result = await fetchList("crm/my/tasks", {
        ...tasksPage,
      });
      return result.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: isOpen === true && tasksFilter !== null,
    }
  );

  const openTasksPage = {
    filter: {
      $and: [
        {
          status_id: openStatus,
        },
        { type: { $eq: "task" } },
      ],
    },
  };

  const openItems = useQuery(
    [openTasksPage, tasksReloadTimestamp],
    async () => {
      const result = await fetchList("crm/my/tasks", {
        ...openTasksPage,
      });

      return result.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: openStatus !== false,
    }
  );

  const onEditTask = (r) => {
    setChildrenDrawer(true);
    setActiveRecord(r);
  };

  const hideEditTask = () => {
    setChildrenDrawer(false);
    setActiveRecord(null);
  };

  const onFinishEditTask = () => {
    setChildrenDrawer(false);
  };

  let entity = null;
  if (activeRecord && activeRecord.patient) {
    if (activeRecord.patient.status_id === 3) {
      entity = "crm/patients";
    } else {
      entity = "patients";
    }
  } else if (activeRecord && activeRecord.employee) {
    entity = "employees";
  }

  let entityId = null;
  if (activeRecord && activeRecord.patient) {
    entityId = activeRecord.patient.id;
  } else if (activeRecord && activeRecord.employee) {
    entityId = activeRecord.employee.id;
  }

  let branchId = null;
  if (activeRecord && activeRecord.patient) {
    branchId = activeRecord.patient.branch;
  } else if (activeRecord && activeRecord.employee) {
    branchId = activeRecord.employee.active_branches[0].id;
  }

  const onCollapse = (val) => {
    setCollapsed(val);
  };

  const onChildrenDrawerClose = () => {
    setChildrenDrawer(false);
  };

  const loadMore = () => {
    setLimit(limit + initialLimit);
  };

  const reloadTasks = () => {
    setTasksReloadTimestamp(moment().format());
  };

  return openItems?.data && !openItems.isLoading ? (
    <>
      {openItems?.data?.total !== null && (
        <div style={{ direction: "ltr" }}>
          <Badge
            count={openItems?.data?.total}
            overflowCount={999}
            style={{ transform: "translate(100%, -50%)" }}
          >
            <Button
              type="text"
              icon={<BellOutlined />}
              onClick={() => setIsOpen(!isOpen)}
            />
          </Badge>
        </div>
      )}

      <Drawer
        width={collapsed ? 650 : 750}
        styles={{
          body: {
            padding: 0,
          },
        }}
        closable={false}
        onClose={() => setIsOpen(false)}
        open={isOpen}
        placement={"left"}
      >
        <ToDoListWrapper>
          <motion.div
            variants={variants}
            animate={isOpen ? "open" : "closed"}
            className="container"
          >
            <Layout style={{ minWidth: "630px", height: "100%" }}>
              <ToDoHeader minimize={minimize} />
              <Layout>
                <ToDoSider
                  changeContentTitle={changeContentTitle}
                  onChangeFilters={onChangeFilters}
                  onChangeMenuFilter={onChangeMenuFilter}
                  filters={filters}
                  onCollapse={onCollapse}
                  collapsed={collapsed}
                />
                <Layout
                  style={{
                    padding: "0 24px",
                    width: "550px",
                    background: "#F3F6FB",
                  }}
                >
                  <Text style={{ padding: "10px 0" }}>{contentTitle}</Text>
                  <Content
                    style={{
                      padding: 0,
                      margin: 0,
                      minHeight: 280,
                      background: "#F3F6FB",
                      maxHeight: window.innerHeight - 82,
                      overflow: "auto",
                    }}
                  >
                    <TasksList
                      isOpen={isOpen}
                      items={data?.items}
                      onEditTask={onEditTask}
                      onReload={reloadTasks} // Pass the reload callback
                    />
                    {data?.total > limit && (
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                          paddingBottom: "10px",
                        }}
                      >
                        <Button
                          onClick={loadMore}
                          type="dashed"
                          loading={isLoading}
                        >
                          {intl.formatMessage({
                            id: `app.actions.load_more_activities`,
                          })}
                        </Button>
                      </div>
                    )}
                  </Content>
                </Layout>
              </Layout>
            </Layout>
          </motion.div>
        </ToDoListWrapper>
        <Drawer
          title={null}
          width={520}
          closable={false}
          onClose={onChildrenDrawerClose}
          open={childrenDrawer}
          placement={"left"}
        >
          {childrenDrawer && (
            <EditActivity
              hide={hideEditTask}
              record={activeRecord}
              recordId={activeRecord?.id}
              visible={activeRecord !== null}
              type={activeRecord?.type}
              entity={entity}
              entityId={entityId}
              branchId={branchId}
              onReloadFeed={onFinishEditTask}
            />
          )}
        </Drawer>
      </Drawer>
    </>
  ) : null;
};

export default ToDoList;
